import { apiFetch } from '../lib/apiFetch';
import type { RawOrderWithSections, OrderSection } from './orderService';

/** Label marking the order-closing pickup section (mirrors the backend's
 *  SECTION_FINAL_PICKUP_LABEL). A "Final pickup" is a pickup-only, one-day,
 *  terminal section — no section can follow it. */
export const SECTION_FINAL_PICKUP_LABEL = 'Final pickup';

/** Input for appending a section to an order. Dates are ISO datetimes; omit them
 *  to let the backend default delivery to the prior section's pickup (the swap
 *  hinge) with a standard 7-day window. */
export interface SectionInput {
  starts_at?: string | null;
  stops_at?: string | null;
  phase_label?: string;
  /** Pickup-only terminal section that hauls the last bin away. */
  final_pickup?: boolean;
}

/** One entry of the section schedule batch-save (the fully-resolved chain). */
export interface SectionScheduleItem {
  id: string;
  starts_at: string | null;
  stops_at: string | null;
}

const orderFromResponse = async (response: Response, action: string): Promise<RawOrderWithSections> => {
  if (!response.ok) {
    // Surface the backend reason (e.g. the 422 rejecting a section that falls
    // outside the order window) rather than a generic status line.
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `Failed to ${action}: ${response.statusText}`);
  }
  const data = await response.json();
  return data.data as RawOrderWithSections;
};

/** Append an in-order phase (section) to an order. Returns the updated order. */
export const addSection = async (orderId: string, input: SectionInput = {}): Promise<RawOrderWithSections> => {
  const response = await apiFetch(`/orders/${orderId}/sections`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return orderFromResponse(response, 'add section');
};

/** Edit one section's label and/or window. Returns the updated order. */
export const updateSection = async (
  orderId: string,
  sectionId: string,
  input: { phase_label?: string; starts_at?: string | null; stops_at?: string | null },
): Promise<RawOrderWithSections> => {
  const response = await apiFetch(`/orders/${orderId}/sections/${sectionId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return orderFromResponse(response, 'update section');
};

/** Remove the last section of an order (keeps numbering contiguous). Its lines
 *  detach to ungrouped rather than delete. Returns the updated order. */
export const deleteSection = async (orderId: string, sectionId: string): Promise<RawOrderWithSections> => {
  const response = await apiFetch(`/orders/${orderId}/sections/${sectionId}`, {
    method: 'DELETE',
  });
  return orderFromResponse(response, 'remove section');
};

/** Move an existing line into a phase (section), or unschedule it back to the
 *  base bucket with `sectionId = null`. `lineId` is the Booqable line id (the key
 *  lineSectionMap is keyed by). The backend rejects moves in/out of a billed
 *  (activated) phase. Returns the updated order. */
export const assignLineSection = async (
  orderId: string,
  lineId: string,
  sectionId: string | null,
): Promise<RawOrderWithSections> => {
  const response = await apiFetch(`/orders/${orderId}/lines/${encodeURIComponent(lineId)}/section`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ section_id: sectionId }),
  });
  return orderFromResponse(response, 'move line');
};

/** Persist the whole section schedule atomically. The caller (lib/phaseDates.ts
 *  cascade) sends the fully-resolved contiguous chain; the backend validates
 *  order-window containment and writes every window in one transaction. Returns
 *  the updated order. */
export const saveSectionSchedule = async (
  orderId: string,
  sections: SectionScheduleItem[],
): Promise<RawOrderWithSections> => {
  const response = await apiFetch(`/orders/${orderId}/sections/dates`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sections }),
  });
  return orderFromResponse(response, 'save section schedule');
};

/** Activate (bill) a single section: finalizes the order's invoice so the
 *  section's lines are billed now. Booqable's proration mints a delta invoice on
 *  each subsequent activation; the customer sees one growing balance. Returns
 *  the updated order. */
export const activateSection = async (orderId: string, sectionId: string): Promise<RawOrderWithSections> => {
  const response = await apiFetch(`/orders/${orderId}/sections/${sectionId}/activate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  return orderFromResponse(response, 'activate section');
};

/** Bill several phases as ONE invoice (one finalize), under the order's single
 *  PO — three independently-dispatched bins on a single invoice, say. Every
 *  unbilled phase that already has items on the order must be included (the
 *  finalize bills them all); the backend rejects a partial batch. Returns the
 *  updated order. */
export const activateSections = async (orderId: string, sectionIds: string[]): Promise<RawOrderWithSections> => {
  const response = await apiFetch(`/orders/${orderId}/sections/activate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ section_ids: sectionIds }),
  });
  return orderFromResponse(response, 'bill phases');
};

/** Start just this section's plannings (its bin goes out) while the rest of the
 *  order stays reserved. Returns the updated order. */
export const startSection = async (orderId: string, sectionId: string): Promise<RawOrderWithSections> => {
  const response = await apiFetch(`/orders/${orderId}/sections/${sectionId}/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  return orderFromResponse(response, 'start section');
};

/** Stop just this section's plannings (its bin comes back) while the rest of the
 *  order stays started. Returns the updated order. */
export const stopSection = async (orderId: string, sectionId: string): Promise<RawOrderWithSections> => {
  const response = await apiFetch(`/orders/${orderId}/sections/${sectionId}/stop`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  return orderFromResponse(response, 'stop section');
};

export type { OrderSection };
